import React, { useEffect, useRef, useCallback } from "react";
import {
  SelectTabData,
  SelectTabEvent,
  Subtitle1,
  Tab,
  TabList,
  TabValue,
} from "@fluentui/react-components";
import { useLoaderData } from "react-router-dom";
import { MenuListLoaderData } from "./menuListLoader";
import {
  HelperUtils,
  MenuCategory,
  appInsightsEvents,
  trackEvent,
  useAppStore,
} from "../../../common";
import { useStyles } from "./MenuListStyles";
import { MenuCategoryCard } from "../MenuCategoryCard";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Public Tabster event (TabsterMoveFocusEventName); not re-exported by @fluentui/react-components.
const TABSTER_MOVE_FOCUS_EVENT = "tabster:movefocus";

const isForwardTabKey = (
  event: Pick<KeyboardEvent, "key" | "shiftKey" | "altKey" | "ctrlKey" | "metaKey">,
) => event.key === "Tab" && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;

export const MenuList = () => {
  const styles = useStyles();
  const [context] = useAppStore((state) => [state.context]);
  const appInsights = useAppStore((state) => state.appInsights);
  const [menuListState, setSelectedCategory] = useAppStore((state) => [
    state.menuList,
    state.menuList.setSelectedCategory,
  ]);

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "MenuList",
    });
  }, [appInsights, context]);

  // Move the screen reader cursor to the start of the page content on load.
  useEffect(() => {
    stationHeadingRef.current?.focus({ preventScroll: true });
  }, []);

  //Get data from loader data.
  const loaderData = useLoaderData() as MenuListLoaderData;
  const { menus, station, showOfflineMenus } = loaderData;

  // Values for category tabs
  const [selectedTabId, setSelectedTabId] = React.useState<TabValue>("");
  const [menuCategories, setMenuCategories] = React.useState<MenuCategory[]>([]);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [tabCategory, setTabCategory] = React.useState<TabValue>("");
  const totalItems = menuCategories.reduce((total, category) => total + category.items.length, 0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const stationHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  // Category that currently contains keyboard focus; while set, it drives the selected tab
  // instead of the scroll position.
  const focusedCategoryRef = useRef<string | null>(null);
  const tabListRef = useRef<HTMLDivElement | null>(null);

  const headerOffset = windowWidth <= 499 ? 184 : 20;
  const menuCategoryOffset = windowWidth <= 499 ? 184 : 220;

  const scrollToCategory = (menuCategoryElement: HTMLDivElement | null) => {
    if (menuCategoryElement && scrollContainerRef.current) {
      const elementPosition =
        menuCategoryElement.getBoundingClientRect().top + scrollContainerRef.current.scrollTop;
      const offsetPosition = elementPosition - menuCategoryOffset;
      scrollContainerRef.current.scrollTo({
        top: offsetPosition,
        behavior: "auto",
      });
    }
  };
  // Handle scroll and select the category upon scrolling
  const handleScroll = useCallback(() => {
    if (focusedCategoryRef.current) {
      return;
    }
    let visibleCategory: string | null = null;

    Object.entries(categoryRefs.current).forEach(([id, element]) => {
      if (element) {
        const { top, bottom } = element.getBoundingClientRect();
        const containerTop = scrollContainerRef.current!.getBoundingClientRect().top;
        if (top <= headerOffset + containerTop && bottom >= headerOffset + containerTop) {
          visibleCategory = id;
        }
      }
    });

    // Do not blur the active element here: scroll-spy updates must not steal keyboard focus.
    if (visibleCategory && visibleCategory !== menuListState.selectedCategory) {
      setSelectedCategory(visibleCategory);
      setSelectedTabId(visibleCategory);
      if (
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== stationHeadingRef.current
      ) {
        document.activeElement.blur();
      }
    }
  }, [totalItems, menuListState.selectedCategory]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      handleScroll();
    }

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      handleScroll();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, windowWidth]);

  useEffect(() => {
    handleScroll();
    const menuItemscontainer = scrollContainerRef.current;
    if (menuItemscontainer) {
      menuItemscontainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (menuItemscontainer) {
        menuItemscontainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);
  useEffect(() => {
    if (station && menus && menus.length > 0) {
      const categories = HelperUtils.getInstance().getFilteredGroupedCafeMenusForSelectedDate(
        menus,
        station?.name as string,
        showOfflineMenus,
      );
      categories.sort((a, b) => a.name.localeCompare(b.name));
      setMenuCategories(categories);
      setSelectedTabId(categories[0]?.name);
      document.title = `${station?.name} - Microsoft Dining`;
    }
  }, [menus, station]);

  useEffect(() => {
    const stationHeading = document.getElementById("menu-list-heading");
    if (stationHeading instanceof HTMLHeadingElement) {
      stationHeadingRef.current = stationHeading;
      stationHeading.focus();
    }
  }, [station?.name]);

  useEffect(() => {
    // Restore previously selected category from store
    const selectedValue = menuListState.selectedCategory;
    if (selectedValue && selectedValue !== null) {
      setSelectedTabId(selectedValue);
      setTabCategory(selectedValue);
      const menuCategoryElement = categoryRefs.current[selectedValue];
      scrollToCategory(menuCategoryElement);
    }
  }, [tabCategory]);

  //Handle tab selection and scroll to the selected category
  const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    const selectedValue = data.value as string;
    setSelectedCategory(selectedValue); //Store the category on selection
    setSelectedTabId(data.value);
    const menuCategoryElement = categoryRefs.current[selectedValue];

    scrollToCategory(menuCategoryElement);
  };

  // Tab from the tab list moves focus into the selected category's content (tabpanel behaviour)
  // instead of the first category's items; arrow keys still move between tabs.
  const focusSelectedCategoryContent = (): boolean => {
    const selectedCategoryElement = categoryRefs.current[selectedTabId as string];
    const firstFocusable = selectedCategoryElement?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!firstFocusable) {
      return false;
    }
    firstFocusable.focus({ preventScroll: true });
    scrollToCategory(selectedCategoryElement);
    return true;
  };
  const focusSelectedCategoryContentRef = useRef(focusSelectedCategoryContent);
  focusSelectedCategoryContentRef.current = focusSelectedCategoryContent;

  const onTabListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || !isForwardTabKey(event)) {
      return;
    }
    if (focusSelectedCategoryContent()) {
      event.preventDefault();
    }
  };

  // Fluent's TabList is a Tabster arrow-navigation group. On Tab out of the group Tabster moves
  // focus itself (to the next element in DOM order) and stops the keydown before React sees it,
  // so intercept its cancelable move-focus event and redirect focus to the selected category.
  useEffect(() => {
    const onTabsterMoveFocus = (event: Event) => {
      const relatedEvent = (event as CustomEvent<{ relatedEvent?: KeyboardEvent }>).detail
        ?.relatedEvent;
      if (
        !relatedEvent ||
        !isForwardTabKey(relatedEvent) ||
        !(relatedEvent.target instanceof Node) ||
        !tabListRef.current?.contains(relatedEvent.target)
      ) {
        return;
      }
      if (focusSelectedCategoryContentRef.current()) {
        event.preventDefault();
        relatedEvent.preventDefault();
      }
    };
    document.addEventListener(TABSTER_MOVE_FOCUS_EVENT, onTabsterMoveFocus);
    return () => {
      document.removeEventListener(TABSTER_MOVE_FOCUS_EVENT, onTabsterMoveFocus);
    };
  }, []);

  const onCategoryFocus = (categoryName: string) => {
    focusedCategoryRef.current = categoryName;
    if (menuListState.selectedCategory !== categoryName || selectedTabId !== categoryName) {
      setSelectedCategory(categoryName);
      setSelectedTabId(categoryName);
    }
  };

  const onCategoryBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      focusedCategoryRef.current = null;
    }
  };

  // Mouse-wheel / touch scrolling hands tab selection back to the scroll position, even if a
  // menu item still has focus (e.g. focus from a pointer press that didn't navigate).
  const releaseFocusLock = () => {
    focusedCategoryRef.current = null;
  };

  return (
    <div
      data-testid="menuList"
      className={styles.container}
      onWheel={releaseFocusLock}
      onTouchStart={releaseFocusLock}
    >
      <main
        className={styles.main}
        aria-labelledby="menu-list-heading"
        ref={windowWidth <= 499 ? scrollContainerRef : null}
      >
        <div className={styles.subMainContainer}>
          <div className={styles.tabListContainer}>
            <div className={styles.stationName}>
              <Subtitle1
                as="h1"
                id="menu-list-heading"
                className={styles.stationHeading}
                tabIndex={-1}
              >
                {station?.name as string}
              </Subtitle1>
            </div>
            <TabList
              ref={tabListRef}
              className={styles.tabList}
              size="medium"
              aria-label={`${station?.name as string} menu categories`}
              selectedValue={selectedTabId}
              onTabSelect={onTabSelect}
              onKeyDown={onTabListKeyDown}
            >
              {menuCategories?.map((menuCategory) => {
                return (
                  <Tab
                    id={`menu-category-tab-${menuCategory.id}`}
                    aria-controls={`menu-category-panel-${menuCategory.id}`}
                    value={menuCategory.name}
                    key={menuCategory.id}
                  >
                    {menuCategory.name}
                  </Tab>
                );
              })}
            </TabList>
          </div>
          <div
            className={styles.menuCategoriesContainer}
            ref={windowWidth > 499 ? scrollContainerRef : null}
          >
            {menuCategories?.map((menuCategory, index) => {
              const isLastCategory = index === menuCategories.length - 1;
              return (
                <div
                  key={menuCategory.id}
                  id={`menu-category-panel-${menuCategory.id}`}
                  role="region"
                  aria-labelledby={`menu-category-heading-${menuCategory.id}`}
                  ref={(el) => (categoryRefs.current[menuCategory.name] = el)}
                  className={isLastCategory ? styles.lastCategory : styles.menuCategoryCard}
                  onFocus={() => onCategoryFocus(menuCategory.name)}
                  onBlur={onCategoryBlur}
                >
                  <MenuCategoryCard
                    category={menuCategory}
                    headingId={`menu-category-heading-${menuCategory.id}`}
                  />
                </div>
              );
            })}
            <div className={styles.dietaryLegend} role="note" aria-label="Dietary abbreviations">
              <span>v = Vegetarian</span>
              <span>vg = Vegan</span>
            </div>
            <div className={styles.scrollSpacer} />
          </div>
        </div>
      </main>
    </div>
  );
};
